import { useCallback, useEffect, useState, useRef } from 'react'
import { getKyousouApiClient } from '../services/network'
import { EnterpriseSummary } from '../clients/kyousou_api_client'
import { BusinessIdeaUserEvaluationType } from '../enums/BusinessIdeaUserEvaluation'
import {
  RecommendationSortType,
  RecommendationSortTypeValue,
} from '../types/recommendation'

// 事業アイデアの型定義
export type BusinessRecommendation = {
  businessIdeaId: string
  projectName: string
  businessContentFirst: string
  keyPoint: string
  evaluation: number
  userEvaluation: BusinessIdeaUserEvaluationType | null
  recommendationEnterprise: EnterpriseSummary
}

/**
 * 事業アイデア一覧を取得するフック
 */
export const useOwnRecommendation = (
  limit = 9,
  initialSort: RecommendationSortTypeValue = RecommendationSortType.CREATED_AT
): {
  totalCount: number
  cursor: number
  isLoading: boolean
  isLoadingMore: boolean // 追加ローディング中かどうか
  hasMore: boolean // 追加読み込み可能かどうか
  recommendations: BusinessRecommendation[]
  loadMore: () => void
  changeSort: (newSort: RecommendationSortTypeValue) => void
  currentSort: RecommendationSortTypeValue
  load: (isLoadMore?: boolean) => void
} => {
  const apiClient = getKyousouApiClient()
  const [totalCount, setTotalCount] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [recommendations, setRecommendations] = useState<
    BusinessRecommendation[]
  >([])
  const [currentSort, setCurrentSort] = useState(initialSort)
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasMore = cursor < totalCount && recommendations.length >= limit

  const load = useCallback(
    async (isLoadMore = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }

      try {
        const response = await apiClient.business.getBusiness({
          cursor: isLoadMore ? cursor : 0,
          limit,
          sort: currentSort,
        })
        const newRecommendations = response.data.businessList
        setTotalCount(response.data.total)

        if (!abortControllerRef.current?.signal.aborted) {
          setRecommendations((prevRecommendations) => {
            if (!isLoadMore) return newRecommendations
            return [...prevRecommendations, ...newRecommendations]
          })

          setCursor((prev) =>
            isLoadMore
              ? prev + newRecommendations.length
              : newRecommendations.length
          )
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('事業アイデアの取得に失敗しました:', error)
          if (!isLoadMore) {
            setRecommendations([])
            setTotalCount(0)
            setCursor(0)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, currentSort, cursor]
  )

  useEffect(() => {
    load(false)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [currentSort])

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    load(true)
  }, [isLoadingMore, hasMore, isLoading])

  const changeSort = useCallback(
    (newSort: RecommendationSortTypeValue) => {
      if (newSort !== currentSort) {
        setCurrentSort(newSort)
        setRecommendations([])
        setTotalCount(0)
        setCursor(0)
      }
    },
    [currentSort]
  )

  return {
    totalCount,
    cursor,
    isLoading,
    isLoadingMore,
    hasMore,
    recommendations,
    loadMore,
    changeSort,
    currentSort,
    load,
  }
}
